import { selectCurrUser } from '@/store/bootstrap/selectors';
import { selectUsers } from '@/store/users/selectors';
import { useSelector } from 'react-redux';

export const useProjectAccess = (projectDetails = {}) => {
  const currUser = useSelector(selectCurrUser);
  const allUsers = useSelector(selectUsers);

  const { project_collaborators = [], status, access } = projectDetails;

  const projectOwner = project_collaborators?.find(
    (proj) => proj?.access === 'project_owner'
  );

  const isCurruserOwner = projectOwner?.user?.id === currUser?.id;
  const isProjectArchived = status === 'archived';
  const isCurruserOnlyViewer =
    project_collaborators?.find((proj) => proj?.user?.id === currUser?.id)
      ?.access === 'project_viewer' ||
    (access === 'all_users' &&
      allUsers?.find((user) => user?.id === currUser?.id)?.role_name ===
        'hiring_manager');

  const allowedRolesForCreatingReview = ['owner', 'admin', 'recruiter'];
  const allowedRolesForViewing = [
    ...allowedRolesForCreatingReview,
    'hiring_manager',
  ];
  if (access === 'all_users')
    allowedRolesForCreatingReview.push('hiring_manager');

  const allowedAllUsersForCreatingReview = allUsers?.filter(
    (user) =>
      allowedRolesForCreatingReview.includes(user?.role_name) &&
      user?.id !== currUser?.id
  );

  const allowedAllUsersForViewing = allUsers?.filter(
    (user) =>
      allowedRolesForViewing.includes(user?.role_name) &&
      user?.id !== currUser?.id
  );

  const isCurruserHasEditAccess =
    project_collaborators?.find((proj) => proj?.user?.id === currUser?.id)
      ?.access !== 'project_viewer' ||
    (access === 'all_users' &&
      allowedAllUsersForCreatingReview?.some(
        (user) => user.id === currUser?.id
      ));

  const collaboratorsOptions = project_collaborators
    ?.filter((collaborator) => collaborator?.user?.id !== currUser?.id)
    ?.map((collaborator) => ({
      ...(collaborator?.user && {
        ...collaborator.user,
        id: collaborator.user.id,
      }),
      ...(collaborator?.account_invitation && {
        ...collaborator?.account_invitation,
        id: `${collaborator?.id}--*--reviewer_id`,
        id_type: 'account_invitation',
        isUnverifiedExternalUser: true,
        currentFormKey: 'reviewer_id',
      }),
      collabId: collaborator?.id,
    }));

  const allowedCollaboratorsOptionsForCreateReview = [
    ...(access === 'all_users' ? allowedAllUsersForCreatingReview : []),
    ...collaboratorsOptions,
  ]?.reduce((uniqueUsers, user) => {
    const existingUser = uniqueUsers?.find((u) => u.id === user.id);
    if (!existingUser) uniqueUsers.push(user);
    return uniqueUsers;
  }, []);

  const allowedCollaboratorsOptionsForViewing = [
    ...(access === 'all_users' ? allowedAllUsersForViewing : []),
    ...collaboratorsOptions,
  ]?.reduce((uniqueUsers, user) => {
    const existingUser = uniqueUsers?.find((u) => u.id === user.id);
    if (!existingUser) uniqueUsers.push(user);
    return uniqueUsers;
  }, []);

  return {
    isCurruserOwner,
    isProjectArchived,
    isCurruserHasEditAccess,
    isCurruserOnlyViewer,
    allowedCollaboratorsOptionsForCreateReview,
    allowedCollaboratorsOptionsForViewing,
  };
};
